import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import type { CustodyAuditRecord } from '@siliconx/shared';
import { CUSTODY_LIFECYCLE_EVENT_TYPE_LABELS, CUSTODY_RECORD_TYPE_LABELS } from '@siliconx/shared';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { DataTable } from '@siliconx/shared/ui/DataTable';
import { ErrorState, LoadingShell } from '@siliconx/shared/ui/state';
import { cn } from '@siliconx/shared/ui/utils';
import { api, throwIfNotOk } from '../lib/api';
import { formatTs, formatUsd } from '../lib/format';
import { qk } from '../lib/queryKeys';
import { requireOrgFeature } from '../lib/orgFeatureGate';

/**
 * SIL-574 (I2-D2, wayfinder-iter2-spec.md §5, §7-D2) — Custody & Audit
 * Records, Intelligence › Outcomes & Reports. A registry read model over
 * EXISTING records (GET /api/client/custody-audit-records): ownership
 * transfers (asset_ownership_event) and lifecycle/location/team change
 * events (asset_lifecycle_event). See ../../../packages/shared/src/dto/
 * custody-audit-registry.ts for the full schema survey — ITAD/sanitization
 * certificates and stored transfer signatures do NOT exist in the schema
 * today and are silently omitted (no "coming soon" row), per the honest-data
 * gate this issue calls for.
 *
 * LEGAL TOUCHPOINT (spec §9) — flagged wording, reviewed here deliberately:
 *   - Page title "Custody & Audit Records" (issue-specified) uses "Records",
 *     never "certificate(s)".
 *   - Subtitle/empty-state copy says a transfer/event was "recorded by
 *     SiliconX" — a factual statement about what this system wrote, not a
 *     claim that SiliconX inspected, verified, or certified the underlying
 *     hardware/data-sanitization event.
 *   - Record-type labels are "Ownership transfer" / "Lifecycle event" — no
 *     "certified", "verified", "assured", "attested", "compliant", or
 *     "audited" (in the assurance sense) anywhere in this file.
 *   - Because no ITAD/sanitization/signature record type is rendered, this
 *     page makes NO claim about data sanitization or destruction having
 *     occurred, certified or otherwise — that's the honest-data gate at
 *     work, not a wording choice to review further.
 * FLAGGED-WORDING LIST FOR LEGAL (submit as-is, nothing further to redline
 * found in this file): "recorded by SiliconX" (subtitle + empty state),
 * "Ownership transfer" / "Lifecycle event" (record-type labels), "Custody &
 * Audit Records" (page title, issue-specified — not authored here).
 */
// SIL-1108: `orgFeature: 'custody'` (customerNav.ts) is business-only —
// gated here so a direct hit doesn't fire this page's mount-time fetch
// before the root `OrgFeatureGate`'s (__root.tsx) redirect lands.
export const Route = createFileRoute('/custody-audit-records')({
  beforeLoad: () => requireOrgFeature('custody'),
  component: CustodyAuditRecordsPage,
});

function RecordTypeBadge({ record }: { record: CustodyAuditRecord }) {
  const isTransfer = record.recordType === 'ownership_transfer';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
        isTransfer
          ? 'bg-sky-50 text-sky-700 ring-sky-200'
          : 'bg-violet-50 text-violet-700 ring-violet-200',
      )}
    >
      {CUSTODY_RECORD_TYPE_LABELS[record.recordType]}
    </span>
  );
}

function EventSummary({ record }: { record: CustodyAuditRecord }) {
  if (record.recordType === 'ownership_transfer') {
    const dir = record.direction === 'outgoing' ? 'Sold to' : 'Acquired from';
    return (
      <span className="text-foreground">
        {dir} {record.counterpartyOrgName ?? 'an unnamed org'}
      </span>
    );
  }
  const typeLabel = record.eventType ? CUSTODY_LIFECYCLE_EVENT_TYPE_LABELS[record.eventType] : 'Event';
  const from = record.fromLabel ?? '—';
  const to = record.toLabel ?? '—';
  return (
    <span className="text-foreground">
      {typeLabel}: {from} → {to}
    </span>
  );
}

function columns(): ColumnDef<CustodyAuditRecord, unknown>[] {
  return [
    {
      id: 'recordType',
      header: 'Record type',
      cell: (ctx) => <RecordTypeBadge record={ctx.row.original} />,
    },
    {
      id: 'asset',
      header: 'Asset',
      cell: (ctx) => {
        const r = ctx.row.original;
        const title =
          r.manufacturerName && r.skuModelName
            ? `${r.manufacturerName} ${r.skuModelName}`
            : (r.serialNumber ?? r.assetTag ?? (r.assetId ? r.assetId.slice(0, 8) : '—'));
        return (
          <div>
            <div className="font-medium text-foreground">{title}</div>
            {r.serialNumber && <div className="text-xs text-muted-foreground">{r.serialNumber}</div>}
          </div>
        );
      },
    },
    {
      id: 'summary',
      header: 'Event',
      cell: (ctx) => <EventSummary record={ctx.row.original} />,
    },
    {
      id: 'amount',
      header: 'Amount',
      cell: (ctx) => {
        const amt = ctx.row.original.amountUsd;
        return <span className="text-foreground">{amt != null ? formatUsd(Number(amt)) : '—'}</span>;
      },
    },
    {
      id: 'occurredAt',
      header: 'Date',
      cell: (ctx) => <span className="text-muted-foreground">{formatTs(ctx.row.original.occurredAt)}</span>,
    },
  ];
}

function CustodyAuditRecordsPage() {
  const [assetFilter, setAssetFilter] = useState<string>('all');
  const [listingFilter, setListingFilter] = useState<string>('all');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const registryQ = useQuery<{ data: CustodyAuditRecord[] }>({
    queryKey: qk.custodyAuditRecords.all(),
    queryFn: async () => {
      const res = await api.api.client['custody-audit-records'].$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { data: CustodyAuditRecord[] };
    },
  });

  const allRows = registryQ.data?.data ?? [];

  const assetOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of allRows) {
      if (!r.assetId) continue;
      const label =
        r.manufacturerName && r.skuModelName
          ? `${r.manufacturerName} ${r.skuModelName}`
          : (r.serialNumber ?? r.assetTag ?? r.assetId.slice(0, 8));
      if (!map.has(r.assetId)) map.set(r.assetId, label);
    }
    return Array.from(map.entries());
  }, [allRows]);

  const listingOptions = useMemo(
    () => Array.from(new Set(allRows.map((r) => r.listingId).filter((id): id is string => id != null))),
    [allRows],
  );

  const rows = useMemo(() => {
    return allRows.filter((r) => {
      if (assetFilter !== 'all' && r.assetId !== assetFilter) return false;
      if (listingFilter !== 'all' && r.listingId !== listingFilter) return false;
      if (fromDate && (!r.occurredAt || r.occurredAt < fromDate)) return false;
      if (toDate && (!r.occurredAt || r.occurredAt > `${toDate}T23:59:59.999Z`)) return false;
      return true;
    });
  }, [allRows, assetFilter, listingFilter, fromDate, toDate]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Custody & Audit Records"
        pinTo="/custody-audit-records"
        subtitle="Every ownership transfer and lifecycle event recorded by SiliconX for your fleet — a registry over real records, not a compliance certificate."
      />

      {registryQ.data && allRows.length > 0 && (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3">
          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
            Asset
            <select
              value={assetFilter}
              onChange={(e) => setAssetFilter(e.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
            >
              <option value="all">All assets</option>
              {assetOptions.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          {listingOptions.length > 0 && (
            <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
              Transaction
              <select
                value={listingFilter}
                onChange={(e) => setListingFilter(e.target.value)}
                className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
              >
                <option value="all">All transactions</option>
                {listingOptions.map((id) => (
                  <option key={id} value={id}>
                    {id.slice(0, 8)}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
            From
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
            />
          </label>

          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
            To
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
            />
          </label>

          {(assetFilter !== 'all' || listingFilter !== 'all' || fromDate || toDate) && (
            <button
              type="button"
              onClick={() => {
                setAssetFilter('all');
                setListingFilter('all');
                setFromDate('');
                setToDate('');
              }}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {registryQ.isLoading && <LoadingShell rows={6} />}
      {registryQ.isError && (
        <ErrorState
          title="Could not load Custody & Audit Records"
          error={registryQ.error}
          onRetry={() => void registryQ.refetch()}
        />
      )}

      {registryQ.data &&
        (allRows.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center">
            <p className="text-muted-foreground">No custody or audit records yet.</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center">
            <p className="text-muted-foreground">No records match the current filters.</p>
          </div>
        ) : (
          <DataTable data={rows} columns={columns()} />
        ))}
    </div>
  );
}
